import React, { useState } from 'react';
import { ClipboardList, ChevronDown, ChevronRight, CheckCircle, XCircle, Trash2, User, Car, AlertTriangle } from 'lucide-react';
import { approveVisitRequest, rejectVisitRequest, deleteVisitRequest, getVisitRequest } from '../../api/visitors';

const C = {
  bg: '#0a0e17', surface: '#131a2b', elevated: '#1a2235',
  border: 'rgba(255,255,255,0.08)', text: '#e2e8f0', muted: '#64748b',
  subtle: '#94a3b8', green: '#22c55e', red: '#ef4444', blue: '#3b82f6',
  amber: '#f59e0b', teal: '#009688', purple: '#a78bfa',
};

const STATUS_COLORS = { PENDING: C.amber, APPROVED: C.green, REJECTED: C.red };
const PRIORITY_COLORS = { LOW: C.muted, NORMAL: C.blue, HIGH: C.amber, URGENT: C.red };

function StatusBadge({ status }) {
  return <span style={{ padding: '3px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: 700, background: STATUS_COLORS[status] + '22', color: STATUS_COLORS[status] }}>{status}</span>;
}
function PriorityBadge({ priority }) {
  return <span style={{ padding: '3px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: 600, background: PRIORITY_COLORS[priority] + '22', color: PRIORITY_COLORS[priority] }}>{priority}</span>;
}

function RejectModal({ onConfirm, onClose }) {
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', zIndex: 300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: '14px', padding: '24px', width: '380px', maxWidth: '95vw' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
          <XCircle size={20} color={C.red} />
          <div style={{ fontSize: '15px', fontWeight: 700, color: C.text }}>Reject Visit Request</div>
        </div>
        <div style={{ fontSize: '13px', color: C.subtle, marginBottom: '12px' }}>Please provide a reason for rejection.</div>
        <textarea
          style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: `1px solid ${C.border}`, background: C.elevated, color: C.text, fontSize: '13px', fontFamily: 'inherit', resize: 'vertical', minHeight: '90px', boxSizing: 'border-box' }}
          placeholder="Rejection reason…"
          value={reason}
          onChange={e => setReason(e.target.value)}
        />
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '16px' }}>
          <button onClick={onClose} style={{ padding: '9px 18px', borderRadius: '8px', border: `1px solid ${C.border}`, background: 'transparent', color: C.muted, fontSize: '13px', cursor: 'pointer', fontFamily: 'inherit' }}>Cancel</button>
          <button onClick={async () => { if (!reason.trim()) return; setSubmitting(true); await onConfirm(reason.trim()); setSubmitting(false); }} disabled={submitting || !reason.trim()} style={{ padding: '9px 18px', borderRadius: '8px', border: 'none', background: C.red, color: '#fff', fontSize: '13px', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', opacity: submitting || !reason.trim() ? 0.7 : 1 }}>
            {submitting ? 'Rejecting…' : 'Reject'}
          </button>
        </div>
      </div>
    </div>
  );
}

function ExpandedDetail({ vrId, tenantId }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);

  React.useEffect(() => {
    setLoading(true);
    getVisitRequest(vrId).then(d => { setDetail(d); setLoading(false); }).catch(() => setLoading(false));
  }, [vrId]);

  if (loading) return <tr><td colSpan={8} style={{ padding: '16px 20px', color: C.muted, fontSize: '13px' }}>Loading details…</td></tr>;
  if (!detail) return null;

  const subTh = { padding: '6px 10px', fontSize: '11px', fontWeight: 700, color: C.muted, borderBottom: `1px solid ${C.border}` };
  const subTd = { padding: '6px 10px', fontSize: '12px', color: C.subtle, borderBottom: `1px solid ${C.border}` };

  return (
    <tr>
      <td colSpan={8} style={{ background: C.bg, padding: '16px 20px' }}>
        {detail.rejectionReason && (
          <div style={{ background: C.red + '15', border: `1px solid ${C.red}33`, borderRadius: '8px', padding: '10px 14px', marginBottom: '16px', fontSize: '13px', color: C.red }}>
            <strong>Rejection Reason:</strong> {detail.rejectionReason}
          </div>
        )}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
          {/* Personnel */}
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '8px' }}><User size={13} color={C.teal} /><span style={{ fontSize: '11px', fontWeight: 700, color: C.teal, textTransform: 'uppercase' }}>Personnel</span></div>
            <table style={{ width: '100%', borderCollapse: 'collapse', background: C.surface, borderRadius: '8px', overflow: 'hidden' }}>
              <thead><tr>{['Full Name','Nationality','ID/Passport','Phone','Job Title'].map(h => <th key={h} style={subTh}>{h}</th>)}</tr></thead>
              <tbody>
                {detail.personnel?.length === 0 ? <tr><td colSpan={5} style={{ ...subTd, textAlign: 'center' }}>None</td></tr>
                : detail.personnel?.map((p, i) => (
                  <tr key={i}>
                    <td style={{ ...subTd, color: C.text, fontWeight: 600 }}>{p.fullName}</td>
                    <td style={subTd}>{p.nationality || '—'}</td>
                    <td style={subTd}>{p.idPassportNumber || '—'}</td>
                    <td style={subTd}>{p.phoneNumber || '—'}</td>
                    <td style={subTd}>{p.jobTitle || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {/* Vehicles */}
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '8px' }}><Car size={13} color={C.blue} /><span style={{ fontSize: '11px', fontWeight: 700, color: C.blue, textTransform: 'uppercase' }}>Vehicles</span></div>
            <table style={{ width: '100%', borderCollapse: 'collapse', background: C.surface, borderRadius: '8px', overflow: 'hidden' }}>
              <thead><tr>{['Vehicle Type','Plate Number'].map(h => <th key={h} style={subTh}>{h}</th>)}</tr></thead>
              <tbody>
                {detail.vehicles?.length === 0 ? <tr><td colSpan={2} style={{ ...subTd, textAlign: 'center' }}>None</td></tr>
                : detail.vehicles?.map((v, i) => (
                  <tr key={i}>
                    <td style={subTd}>{v.vehicleType || '—'}</td>
                    <td style={subTd}>{v.plateNumber || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        {detail.visitRemark && <div style={{ marginTop: '14px', fontSize: '13px', color: C.subtle }}><strong style={{ color: C.muted }}>Remark:</strong> {detail.visitRemark}</div>}
      </td>
    </tr>
  );
}

const STATUSES = ['ALL', 'PENDING', 'APPROVED', 'REJECTED'];

export default function VisitRequestsView({ visitRequests, sites, scopedSiteId, onUpdate, onRemove }) {
  const [filterStatus, setFilterStatus] = useState('ALL');
  const [filterSite, setFilterSite] = useState('');
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState({});
  const [rejectTarget, setRejectTarget] = useState(null);
  const [acting, setActing] = useState({});

  const effectiveSiteId = scopedSiteId || filterSite;
  const scopedSite = scopedSiteId ? sites.find(s => s.id === scopedSiteId) : null;

  function toggleExpand(id) { setExpanded(e => ({ ...e, [id]: !e[id] })); }

  const scopedRequests = scopedSiteId
    ? visitRequests.filter(vr => vr.siteId === scopedSiteId)
    : visitRequests;

  const filtered = scopedRequests.filter(vr =>
    (filterStatus === 'ALL' || vr.status === filterStatus) &&
    (!effectiveSiteId || vr.siteId === effectiveSiteId) &&
    (!search || vr.visitorCompanyName?.toLowerCase().includes(search.toLowerCase()) || vr.visitReason?.toLowerCase().includes(search.toLowerCase()))
  );

  async function handleApprove(vr) {
    setActing(a => ({ ...a, [vr.id]: 'approving' }));
    try { const updated = await approveVisitRequest(vr.id); onUpdate(updated); }
    catch (e) { alert(e.response?.data?.error || 'Failed to approve'); }
    setActing(a => ({ ...a, [vr.id]: null }));
  }

  async function handleReject(vr, reason) {
    try { const updated = await rejectVisitRequest(vr.id, reason); onUpdate(updated); setRejectTarget(null); }
    catch (e) { alert(e.response?.data?.error || 'Failed to reject'); }
  }

  async function handleDelete(vr) {
    if (!window.confirm(`Delete this PENDING visit request from ${vr.visitorCompanyName}?`)) return;
    try { await deleteVisitRequest(vr.id); onRemove(vr.id); }
    catch (e) { alert(e.response?.data?.error || 'Failed to delete'); }
  }

  const th = { padding: '10px 14px', textAlign: 'left', fontSize: '11px', fontWeight: 700, color: C.muted, borderBottom: `1px solid ${C.border}`, whiteSpace: 'nowrap' };
  const td = { padding: '10px 14px', fontSize: '13px', color: C.text, borderBottom: `1px solid ${C.border}`, verticalAlign: 'middle' };

  return (
    <div style={{ padding: '24px', color: C.text }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px' }}>
        <ClipboardList size={20} color={C.teal} />
        <div>
          <div style={{ fontSize: '17px', fontWeight: 700 }}>Visit Requests{scopedSite ? ` — ${scopedSite.name}` : ''}</div>
          <div style={{ fontSize: '12px', color: C.muted }}>Review and manage visitor registration forms</div>
        </div>
        <div style={{ marginLeft: 'auto', background: C.amber + '22', border: `1px solid ${C.amber}44`, borderRadius: '20px', padding: '3px 12px', fontSize: '12px', fontWeight: 700, color: C.amber }}>
          {scopedRequests.filter(v => v.status === 'PENDING').length} Pending
        </div>
      </div>

      {/* Status tabs */}
      <div style={{ display: 'flex', gap: '6px', marginBottom: '14px', flexWrap: 'wrap' }}>
        {STATUSES.map(s => (
          <button key={s} onClick={() => setFilterStatus(s)} style={{ padding: '6px 14px', borderRadius: '20px', border: `1px solid ${filterStatus === s ? C.teal : C.border}`, background: filterStatus === s ? C.teal + '22' : 'transparent', color: filterStatus === s ? C.teal : C.muted, fontSize: '12px', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>{s}</button>
        ))}
        {!scopedSiteId && (
          <select style={{ padding: '6px 12px', borderRadius: '8px', border: `1px solid ${C.border}`, background: C.surface, color: C.text, fontSize: '13px', fontFamily: 'inherit', marginLeft: '4px' }} value={filterSite} onChange={e => setFilterSite(e.target.value)}>
            <option value="">All Sites</option>
            {sites.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        )}
        <input placeholder="Search company or reason…" style={{ padding: '6px 12px', borderRadius: '8px', border: `1px solid ${C.border}`, background: C.surface, color: C.text, fontSize: '13px', fontFamily: 'inherit', flex: 1, minWidth: '180px' }} value={search} onChange={e => setSearch(e.target.value)} />
      </div>

      {/* Table */}
      <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: '12px', overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={{ ...th, width: '28px' }}></th>
              {(scopedSiteId ? ['Company', 'Dates', 'Priority', 'Status', 'Submitted', 'Actions'] : ['Company', 'Site', 'Dates', 'Priority', 'Status', 'Submitted', 'Actions']).map(h => <th key={h} style={th}>{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr><td colSpan={scopedSiteId ? 7 : 8} style={{ ...td, textAlign: 'center', color: C.muted, padding: '32px' }}>No visit requests found</td></tr>
            ) : filtered.map(vr => (
              <React.Fragment key={vr.id}>
                <tr style={{ cursor: 'pointer' }}>
                  <td style={{ ...td, paddingRight: '4px', paddingLeft: '10px' }} onClick={() => toggleExpand(vr.id)}>
                    {expanded[vr.id] ? <ChevronDown size={14} color={C.teal} /> : <ChevronRight size={14} color={C.muted} />}
                  </td>
                  <td style={{ ...td, fontWeight: 600 }} onClick={() => toggleExpand(vr.id)}>{vr.visitorCompanyName}</td>
                  {!scopedSiteId && <td style={{ ...td, color: C.subtle }} onClick={() => toggleExpand(vr.id)}>{vr.siteName}</td>}
                  <td style={{ ...td, color: C.subtle, fontSize: '12px' }} onClick={() => toggleExpand(vr.id)}>
                    {vr.arrivalDate?.slice(0, 10)} – {vr.departureDate?.slice(0, 10)}
                  </td>
                  <td style={td} onClick={() => toggleExpand(vr.id)}><PriorityBadge priority={vr.priority} /></td>
                  <td style={td} onClick={() => toggleExpand(vr.id)}><StatusBadge status={vr.status} /></td>
                  <td style={{ ...td, color: C.muted, fontSize: '12px' }} onClick={() => toggleExpand(vr.id)}>
                    {vr.submittedAt ? new Date(vr.submittedAt).toLocaleDateString() : '—'}
                  </td>
                  <td style={td}>
                    <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                      {vr.status === 'PENDING' && (<>
                        <button onClick={() => handleApprove(vr)} disabled={acting[vr.id] === 'approving'} style={{ padding: '5px 10px', borderRadius: '7px', border: `1px solid ${C.green}55`, background: C.green + '22', color: C.green, fontSize: '11px', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: '4px', opacity: acting[vr.id] ? 0.7 : 1 }}>
                          <CheckCircle size={12} /> {acting[vr.id] === 'approving' ? '…' : 'Approve'}
                        </button>
                        <button onClick={() => setRejectTarget(vr)} style={{ padding: '5px 10px', borderRadius: '7px', border: `1px solid ${C.red}55`, background: C.red + '22', color: C.red, fontSize: '11px', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <XCircle size={12} /> Reject
                        </button>
                        <button onClick={() => handleDelete(vr)} style={{ padding: '5px 10px', borderRadius: '7px', border: `1px solid ${C.muted}44`, background: C.muted + '11', color: C.muted, fontSize: '11px', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <Trash2 size={12} />
                        </button>
                      </>)}
                    </div>
                  </td>
                </tr>
                {expanded[vr.id] && <ExpandedDetail vrId={vr.id} />}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>

      {rejectTarget && (
        <RejectModal
          onConfirm={reason => handleReject(rejectTarget, reason)}
          onClose={() => setRejectTarget(null)}
        />
      )}
    </div>
  );
}
